import React, { ReactNode, useEffect, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { SxProps } from '@mui/material'

import { useIsMobile } from 'utils/hooks'

import Box from 'components_new/atoms/Box'
import { IconProps } from 'components_new/atoms/Icon'

import KanbanCard from 'components_new/molecules/KanbanCard'
import KanbanColumn from 'components_new/molecules/KanbanColumn'

export interface KanbanItem {
  columnId: string
  // When true this specific card cannot be dragged (e.g. a locked status).
  disabled?: boolean
  href?: string
  id: string
  search?: string
}

export interface KanbanColumnData {
  actions?: ReactNode
  footer?: ReactNode
  id: string
  icon?: IconProps
  locked?: boolean
  lockedTooltip?: string
  title: string
}

interface KanbanBoardProps {
  columns: KanbanColumnData[]
  // When true the board is read-only: cards cannot be dragged.
  disabled?: boolean
  disableItemPadding?: boolean
  items: KanbanItem[]
  onItemAdd?: (columnId: string) => void
  onItemClick?: (itemId: string) => void
  onItemMove?: (itemId: string, newColumnId: string) => void
  renderItem: (id: string) => ReactNode
  sx?: SxProps
}

const KanbanBoard = (props: KanbanBoardProps) => {
  const {
    columns,
    disabled,
    disableItemPadding,
    items,
    onItemAdd,
    onItemClick,
    onItemMove,
    renderItem,
    sx
  } = props

  const isMobile = useIsMobile()

  const [activeId, setActiveId] = useState<string | null>(null)
  const [itemsState, setItemsState] = useState<KanbanItem[]>(items)
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null)

  // Sync internal state when items prop changes (e.g., from filtering)
  useEffect(() => {
    setItemsState(items)
  }, [items])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event

    if (!over) {
      setActiveColumnId(null)

      return
    }

    const overId = over.id as string

    // Only highlight the target column here. Moving the card between columns
    // mid-drag changes the layout, which can flip the collision back and forth
    // and loop setState ("Maximum update depth exceeded"). The move happens on
    // drop instead; the DragOverlay shows the card meanwhile.
    const overColumn = columns.find((col) => col.id === overId)
    const overItem = itemsState.find((item) => item.id === overId)

    setActiveColumnId(overColumn?.id ?? overItem?.columnId ?? null)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    setActiveId(null)
    setActiveColumnId(null)

    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    const activeItem = itemsState.find((item) => item.id === activeId)

    if (!activeItem) return

    // Determine the target column
    let targetColumnId = activeItem.columnId

    const overColumn = columns.find((col) => col.id === overId)

    if (overColumn) {
      targetColumnId = overColumn.id
    } else {
      const overItem = itemsState.find((item) => item.id === overId)

      if (overItem) {
        targetColumnId = overItem.columnId
      }
    }

    // If moving to a different column
    if (activeItem.columnId !== targetColumnId) {
      const updatedItems = itemsState.map((item) =>
        item.id === activeId ? { ...item, columnId: targetColumnId } : item
      )

      setItemsState(updatedItems)

      if (onItemMove) {
        onItemMove(activeId, targetColumnId)
      }
    } else {
      // Reordering within the same column
      const columnItems = itemsState.filter(
        (item) => item.columnId === activeItem.columnId
      )
      const activeIndex = columnItems.findIndex((item) => item.id === activeId)
      const overIndex = columnItems.findIndex((item) => item.id === overId)

      if (activeIndex !== overIndex && activeIndex !== -1 && overIndex !== -1) {
        const reorderedColumnItems = arrayMove(
          columnItems,
          activeIndex,
          overIndex
        )
        const otherItems = itemsState.filter(
          (item) => item.columnId !== activeItem.columnId
        )

        setItemsState([...otherItems, ...reorderedColumnItems])
      }
    }
  }

  const activeItem = activeId
    ? itemsState.find((item) => item.id === activeId)
    : null

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          ...sx
        }}
      >
        {columns.map((column) => {
          const columnItems = itemsState.filter(
            (item) => item.columnId === column.id
          )
          const itemIds = columnItems.map((item) => item.id)

          return (
            <KanbanColumn
              actions={column.actions}
              count={columnItems.length}
              footer={column.footer}
              icon={column.icon}
              id={column.id}
              isActive={activeColumnId === column.id}
              itemIds={itemIds}
              key={column.id}
              locked={column.locked}
              lockedTooltip={column.lockedTooltip}
              onAddItem={onItemAdd ? () => onItemAdd(column.id) : undefined}
              sx={isMobile ? { minWidth: '80%', width: '80%' } : undefined}
              title={column.title}
            >
              {columnItems.map((item) => (
                <KanbanCard
                  {...item}
                  disabled={disabled || item.disabled}
                  disablePadding={disableItemPadding}
                  key={item.id}
                  onClick={onItemClick ? () => onItemClick(item.id) : undefined}
                >
                  {renderItem(item.id)}
                </KanbanCard>
              ))}
            </KanbanColumn>
          )
        })}
      </Box>

      <DragOverlay>
        {activeItem ? (
          <KanbanCard
            {...activeItem}
            disablePadding={disableItemPadding}
            href={undefined}
            sx={{ cursor: 'grabbing!important' }}
          >
            {renderItem(activeItem.id)}
          </KanbanCard>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default KanbanBoard
